import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link to="/" className="legal-back">
          <span className="back-arrow">&larr;</span> NS Gaming Studio
        </Link>
      </header>

      <main className="legal-content">
        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last Updated: March 4, 2026</p>

        <p>
          NeuralSoft ("Company," "we," "us," or "our") operates NS Gaming Studio ("Service"), an educational
          platform where young people learn to code by building games with AI. This Privacy Policy explains
          how we collect, use, disclose, and protect information from users of the Service, including
          children under the age of 13. We are committed to complying with the Children's Online Privacy
          Protection Act ("COPPA") and all other applicable privacy laws.
        </p>

        <h2>1. Information We Collect</h2>

        <h3>1.1 Account Information</h3>
        <p>
          When you register for an account, we collect the following:
        </p>
        <ul>
          <li><strong>Email address</strong> — used for account authentication, password resets, and essential service communications</li>
          <li><strong>Display name</strong> — a username chosen by the user (we encourage the use of a nickname rather than a real name)</li>
          <li><strong>Avatar selection</strong> — an emoji chosen by the user as a profile picture</li>
          <li><strong>Authentication provider data</strong> — if you sign in via Google, we receive your name, email, and profile photo from Google</li>
        </ul>

        <h3>1.2 User-Generated Content</h3>
        <p>
          When you use the Service, we store:
        </p>
        <ul>
          <li><strong>Game code</strong> — the HTML, CSS, and JavaScript code created through the AI assistant</li>
          <li><strong>Chat messages</strong> — the conversations between the user and the AI assistant, used to provide context for ongoing game development</li>
          <li><strong>Project metadata</strong> — project titles, descriptions, author names, creation/modification timestamps</li>
          <li><strong>Published games</strong> — if a user chooses to publish a game via a shareable link</li>
        </ul>

        <h3>1.3 Usage Data</h3>
        <p>
          We automatically collect certain technical and usage information:
        </p>
        <ul>
          <li><strong>AI usage metrics</strong> — daily and monthly message counts per user, used to enforce plan limits</li>
          <li><strong>Achievement and progress data</strong> — badges earned, projects created, and other engagement statistics</li>
          <li><strong>Error logs</strong> — anonymized error reports to help us improve the Service</li>
          <li><strong>Session data</strong> — login timestamps and session duration for security monitoring</li>
        </ul>

        <h3>1.4 Information We Do NOT Collect</h3>
        <p>
          We do not knowingly collect:
        </p>
        <ul>
          <li>Real names (beyond what a user voluntarily provides as a display name)</li>
          <li>Physical addresses, phone numbers, or government-issued identification</li>
          <li>Location data or GPS coordinates</li>
          <li>Photos, videos, or audio recordings</li>
          <li>Financial information from children (payments are handled by parents through third-party processors)</li>
          <li>Information from cookies used for advertising or behavioral tracking</li>
        </ul>

        <h2>2. How We Use Information</h2>
        <p>We use the information we collect for the following purposes:</p>
        <ul>
          <li><strong>Provide the Service</strong> — to operate the game builder, AI assistant, and related features</li>
          <li><strong>Authentication and security</strong> — to verify identity, prevent unauthorized access, and protect user accounts</li>
          <li><strong>Usage enforcement</strong> — to apply plan-based usage limits and prevent abuse</li>
          <li><strong>Service improvement</strong> — to identify and fix bugs, improve performance, and develop new features</li>
          <li><strong>Communication</strong> — to send essential account-related notices (e.g., password resets, service disruptions). We do not send marketing emails to children.</li>
        </ul>

        <h2>3. Children's Privacy (COPPA Compliance)</h2>
        <p>
          We take children's privacy seriously. The following provisions apply specifically to users under 13:
        </p>

        <h3>3.1 Parental Consent</h3>
        <p>
          We require verifiable parental consent before collecting personal information from children under 13.
          A parent or legal guardian must create or approve the creation of a child's account.
          Parents may review their child's personal information, request its deletion, or revoke consent
          at any time by contacting us at the address listed in Section 10.
        </p>

        <h3>3.2 Limited Collection</h3>
        <p>
          We collect only the minimum information necessary to provide the Service. We do not condition
          a child's participation on disclosing more personal information than is reasonably necessary.
        </p>

        <h3>3.3 No Behavioral Advertising</h3>
        <p>
          We do not serve advertisements to any users. We do not use children's personal information
          for targeted advertising, profiling, or marketing purposes of any kind.
        </p>

        <h3>3.4 No Unrestricted Public Sharing</h3>
        <p>
          When a child publishes a game via a shareable link, only the game content (code) and the display
          name are visible. The child's email address, usage data, and other personal information are never
          exposed. Published games can be unpublished at any time by the account holder.
        </p>

        <h3>3.5 Parental Rights</h3>
        <p>
          Parents and guardians have the right to:
        </p>
        <ul>
          <li>Review the personal information we have collected from their child</li>
          <li>Request deletion of their child's personal information and account</li>
          <li>Refuse to permit further collection or use of their child's information</li>
          <li>Consent to collection and use of their child's information without consenting to disclosure to third parties</li>
        </ul>
        <p>
          To exercise any of these rights, please contact us using the information in Section 10.
          We will verify your identity as the child's parent or guardian before fulfilling any request.
        </p>

        <h2>4. How We Share Information</h2>
        <p>We do not sell, rent, or trade personal information. We may share information in the following limited circumstances:</p>
        <ul>
          <li><strong>AI service providers</strong> — Chat messages and game code are sent to third-party AI providers (Anthropic, OpenAI, and/or Google) to generate AI responses. These providers process data according to their own privacy policies and data processing agreements. We do not send user email addresses or account information to AI providers.</li>
          <li><strong>Infrastructure providers</strong> — We use Google Firebase for hosting, authentication, and data storage. Firebase processes data in accordance with Google's data processing terms.</li>
          <li><strong>Legal compliance</strong> — We may disclose information if required to do so by law, regulation, legal process, or governmental request, or to protect the rights, safety, or property of NeuralSoft, our users, or the public.</li>
          <li><strong>Business transfers</strong> — In the event of a merger, acquisition, or sale of assets, user information may be transferred as part of that transaction. We will notify users of any such change in ownership or control.</li>
        </ul>

        <h2>5. Data Security</h2>
        <p>
          We implement reasonable technical and organizational measures to protect user data, including:
        </p>
        <ul>
          <li>Encrypted data transmission (HTTPS/TLS)</li>
          <li>Firebase Authentication with secure session management</li>
          <li>Firestore security rules restricting data access to authorized users only</li>
          <li>Rate limiting to prevent abuse</li>
          <li>Regular security reviews and monitoring</li>
        </ul>
        <p>
          No method of transmission or storage is 100% secure. While we strive to protect personal information,
          we cannot guarantee its absolute security. If we become aware of a data breach affecting personal
          information, we will notify affected users and, where applicable, relevant authorities in accordance
          with applicable law.
        </p>

        <h2>6. Data Retention</h2>
        <p>
          We retain personal information for as long as the user's account is active or as needed to provide
          the Service. Specifically:
        </p>
        <ul>
          <li><strong>Account data</strong> — retained until the account is deleted by the user or parent</li>
          <li><strong>Game projects and code</strong> — retained until deleted by the user or upon account deletion</li>
          <li><strong>Chat messages</strong> — retained for the duration of the project to maintain conversation context; deleted when the project is deleted</li>
          <li><strong>Usage metrics</strong> — retained for billing and abuse-prevention purposes; anonymized or deleted upon account deletion</li>
          <li><strong>Error logs</strong> — retained for up to 90 days for debugging purposes, then automatically purged</li>
        </ul>
        <p>
          When a parent or user requests account deletion, we will delete or anonymize all associated personal
          information within 30 days, except where retention is required by law.
        </p>

        <h2>7. Cookies and Tracking</h2>
        <p>
          The Service uses only essential cookies and local storage necessary for authentication and session
          management. We do not use analytics cookies, advertising cookies, or any third-party tracking
          technologies. We do not participate in cross-site tracking or behavioral advertising.
        </p>

        <h2>8. Third-Party Links</h2>
        <p>
          The Service may contain links to third-party websites or services. We are not responsible for the
          privacy practices of these third parties. We encourage users and parents to review the privacy
          policies of any third-party services before providing personal information.
        </p>

        <h2>9. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, particularly
          changes affecting the collection or use of children's personal information, we will notify users
          by posting the updated policy on the Service, updating the "Last Updated" date, and, where
          required by COPPA, obtaining renewed parental consent. Your continued use of the Service after
          the changes take effect constitutes acceptance of the revised policy.
        </p>

        <h2>10. Contact Us</h2>
        <p>
          If you have questions about this Privacy Policy, wish to exercise your parental rights under
          COPPA, or need to report a privacy concern, please contact us at:
        </p>
        {/*
          #124 fix (2026-05-17): **CRITICAL COPPA placeholders MUST be filled
          before any production deploy with users under 13**. 16 CFR 312.4(d)
          requires the operator's NAME, ADDRESS, TELEPHONE, EMAIL in the
          privacy policy text — placeholder strings here are an active
          regulatory violation. Fill these in with the real legal entity
          info and have privacy counsel review before deploy. Until that
          happens, this page renders a visible banner so the missing data
          is obvious to any internal reviewer.
        */}
        {(process.env.REACT_APP_ENVIRONMENT === 'production') && (
          <div role="alert" style={{ background: '#fee', border: '2px solid #c00', padding: '12px', margin: '12px 0', borderRadius: '6px' }}>
            <strong>⚠️ DEPLOYMENT BLOCKER:</strong> Contact info placeholders
            below are unfilled. This page must not be served in production —
            #124 in the repair protocol.
          </div>
        )}
        <p>
          NeuralSoft<br />
          Email: [YOUR EMAIL ADDRESS]<br />
          Address: [YOUR MAILING ADDRESS]
        </p>
        <p>
          For COPPA-related inquiries, please include "COPPA Request" in the subject line.
          We will respond to verified parental requests within 30 days.
        </p>

        <h2>11. State-Specific Rights</h2>
        <p>
          Depending on your state of residence, you may have additional privacy rights. For example,
          California residents may have rights under the California Consumer Privacy Act (CCPA) and the
          California Privacy Rights Act (CPRA), including the right to know what personal information
          is collected, the right to request deletion, and the right to opt out of the sale of personal
          information. We do not sell personal information. To exercise any applicable state-specific
          privacy rights, please contact us using the information in Section 10.
        </p>

        <div className="legal-nav-links">
          <Link to="/terms">Terms of Service</Link>
          <Link to="/">Back to Home</Link>
        </div>
      </main>
    </div>
  );
};
